import { Component } from '@angular/core';
import { Input, HostBinding } from '@angular/core';

/**
 * A versatile panel component with sections for header, content, and footer.
 *
 * This component is designed to structure content into a panel layout, supporting configurable size and border visibility.
 * The `upp-panel` serves as the container, while the `upp-panel-header`, `upp-panel-content`, and `upp-panel-footer`
 * components define distinct regions within the panel.
 *
 * ### Features
 * - Configurable panel size (`small` or `large`).
 * - Option to show or hide the border.
 * - Fully customizable content using Angular's `ng-content` for dynamic projection.
 *
 * ### Usage
 * ```html
 * <upp-panel size="large" border="show">
 *   <upp-panel-header>
 *     <h1>Panel Header</h1>
 *   </upp-panel-header>
 *   <upp-panel-content>
 *     <p>Main content goes here.</p>
 *   </upp-panel-content>
 *   <upp-panel-footer>
 *     <button>Submit</button>
 *   </upp-panel-footer>
 * </upp-panel>
 * ```
 *
 * ### Options
 * - **Size (`size`)**:
 *   - `'small'`: Displays the panel with a smaller layout.
 *   - `'large'` (default): Displays the panel with a larger layout.
 * - **Border (`border`)**:
 *   - `'show'` (default): Displays a visible border around the panel.
 *   - `'hide'`: Hides the panel border.
 */
@Component({
    selector: 'upp-panel',
    templateUrl: './upp-panel.html',
    styleUrls: ['./upp-panel.scss'],
})
export class UppPanelComponent {
    /**
     * Sets the size of the panel. Available options:
     * - `'small'`: A smaller panel size.
     * - `'large'`: A larger panel size (default).
     *
     * @default 'large'
     */
    @Input() size: 'small' | 'large' | 'auto' = 'large';

    /**
     * Controls whether the panel border is visible. Available options:
     * - `'show'`: The border is visible (default).
     * - `'hide'`: The border is hidden.
     *
     * @default 'show'
     */
    @Input() lines: 'show' | 'hide' = 'hide';

    /**
     * Dynamically calculates the CSS classes for the panel based on the `size` and `border` inputs.
     *
     * @returns A string representing the classes to apply to the panel.
     */
    @HostBinding('class') get panelClasses(): string {
        const sizeClass = `--${this.size}`;
        const borderClass = `--border-${this.lines}`;
        return `${sizeClass} ${borderClass}`.trim();
    }
}
